import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import ErrorBoundary from "./components/ErrorBoundary";

// Pages
import ComeFunziona from "./pages/ComeFunziona";
import Login from "./pages/Login";
import Register from "./pages/Register";
import RegisterCompany from "./pages/RegisterCompany";
import Offers from "./pages/Offers";
import OfferDetail from "./pages/OfferDetail";
import AdminDashboard from "./pages/AdminDashboard";
import BusinessPlanPage from "./pages/BusinessPlanPage";
import CondoDashboard from "./pages/CondoDashboard";
import CommercialDashboard from "./pages/CommercialDashboard";
import CompanyDashboard from "./pages/CompanyDashboard";
import UserDashboard from "./pages/UserDashboard";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/dashboard/admin" replace />;
      case 'admin_condo':
        return <Navigate to="/dashboard/condo" replace />;
      case 'commercial':
        return <Navigate to="/dashboard/commercial" replace />;
      case 'company':
        return <Navigate to="/dashboard/company" replace />;
      default:
        return <Navigate to="/dashboard/user" replace />;
    }
  }

  return children;
};

// Public Route (redirects to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    switch (user?.role) {
      case 'admin':
        return <Navigate to="/dashboard/admin" replace />;
      case 'admin_condo':
        return <Navigate to="/dashboard/condo" replace />;
      case 'commercial':
        return <Navigate to="/dashboard/commercial" replace />;
      case 'company':
        return <Navigate to="/dashboard/company" replace />;
      default:
        return <Navigate to="/dashboard/user" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Offers />} />
      <Route path="/come-funziona" element={<ComeFunziona />} />
      <Route path="/offers" element={<Offers />} />
      <Route path="/offers/:id" element={<OfferDetail />} />
      <Route path="/proposte/:id" element={<OfferDetail />} />
      <Route path="/register-company" element={<RegisterCompany />} />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        } 
      />
      <Route 
        path="/register" 
        element={
          <PublicRoute>
            <Register />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/dashboard/admin" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/admin/business-plan" 
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <BusinessPlanPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/condo" 
        element={
          <ProtectedRoute allowedRoles={['admin_condo']}>
            <CondoDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/commercial" 
        element={
          <ProtectedRoute allowedRoles={['commercial']}>
            <CommercialDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/company" 
        element={
          <ProtectedRoute allowedRoles={['company']}>
            <CompanyDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard/user" 
        element={
          <ProtectedRoute allowedRoles={['user']}>
            <UserDashboard />
          </ProtectedRoute>
        } 
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
